// Billing Service Manager
// Coordinates between different billing providers and provides unified API

import { BillingProvider, BillingProviderConfig, CheckoutOptions, CheckoutMode, PriceInfo } from './billing-provider';
import { PaddleAdapter } from '../infra/paddle-adapter';
import { StripeAdapter } from '../infra/stripe-adapter';
import { getEnvironmentTier } from '../utils/environment';
import { getEnvConfig } from '../config/env';

export interface BillingServiceConfig {
  paddle?: {
    clientToken: string;
    sandboxClientToken?: string;
    vendorId?: string; // Deprecated - kept for backward compatibility
    sandboxVendorId?: string; // Deprecated
  };
  stripe?: {
    publicKey: string;
    sandboxPublicKey?: string;
  };
}

export class BillingService {
  private providers: Map<string, BillingProvider> = new Map();
  private currentProvider: BillingProvider | null = null;
  private config: BillingServiceConfig;
  private environment: 'sandbox' | 'production';

  constructor(config: BillingServiceConfig) {
    this.config = config;
    this.environment = getEnvironmentTier();
  }

  /**
   * Initialize a specific billing provider
   */
  async initializeProvider(provider: 'paddle' | 'stripe'): Promise<void> {
    // Check if already initialized
    if (this.providers.has(provider)) {
      this.currentProvider = this.providers.get(provider)!;
      return;
    }

    let providerInstance: BillingProvider;
    let providerConfig: BillingProviderConfig;

    switch (provider) {
      case 'paddle':
        if (!this.config.paddle) {
          throw new Error('Paddle configuration not provided');
        }

        // Use clientToken (modern) or fallback to vendorId (legacy)
        const clientToken = this.environment === 'sandbox'
          ? (this.config.paddle.sandboxClientToken || this.config.paddle.clientToken)
          : this.config.paddle.clientToken;

        const vendorId = this.environment === 'sandbox'
          ? (this.config.paddle.sandboxVendorId || this.config.paddle.vendorId)
          : this.config.paddle.vendorId;

        providerConfig = {
          environment: this.environment,
          clientToken: clientToken,
          vendorId: vendorId // Keep for backward compatibility
        };

        providerInstance = new PaddleAdapter();
        break;

      case 'stripe':
        if (!this.config.stripe) {
          throw new Error('Stripe configuration not provided');
        }

        providerConfig = {
          environment: this.environment,
          publicKey: this.environment === 'sandbox'
            ? (this.config.stripe.sandboxPublicKey || this.config.stripe.publicKey)
            : this.config.stripe.publicKey
        };

        providerInstance = new StripeAdapter();
        break;

      default:
        throw new Error(`Unknown billing provider: ${provider}`);
    }

    // Initialize the provider
    await providerInstance.initialize(providerConfig);

    // Store and set as current
    this.providers.set(provider, providerInstance);
    this.currentProvider = providerInstance;

    console.log(`[BillingService] Initialized ${provider} provider`);
  }

  /**
   * Get the current active provider
   */
  getCurrentProvider(): BillingProvider | null {
    return this.currentProvider;
  }

  /**
   * Switch to a different provider
   */
  async switchProvider(provider: 'paddle' | 'stripe'): Promise<void> {
    if (!this.providers.has(provider)) {
      await this.initializeProvider(provider);
    } else {
      this.currentProvider = this.providers.get(provider)!;
    }
  }

  /**
   * Fetch prices using the current provider
   */
  async fetchPrices(priceIds: string[]): Promise<PriceInfo[]> {
    if (!this.currentProvider) {
      throw new Error('No billing provider initialized');
    }
    return this.currentProvider.fetchPrices(priceIds);
  }

  /**
   * Get last address data from price preview (Paddle only)
   */
  getLastAddressData(): { country?: string; postalCode?: string } {
    if (!this.currentProvider) {
      return {};
    }
    // Check if the provider has this method (PaddleAdapter)
    if ('getLastAddressData' in this.currentProvider && typeof this.currentProvider.getLastAddressData === 'function') {
      return this.currentProvider.getLastAddressData();
    }
    return {};
  }

  /**
   * Open checkout with the current provider
   */
  async openCheckout(options: CheckoutOptions, mode: CheckoutMode = 'popup'): Promise<void> {
    if (!this.currentProvider) {
      throw new Error('No billing provider initialized');
    }
    return this.currentProvider.openCheckout(options, mode);
  }

  /**
   * Close checkout
   */
  closeCheckout(): void {
    if (this.currentProvider) {
      this.currentProvider.closeCheckout();
    }
  }

  /**
   * Check if Apple Pay is available
   */
  async isApplePayAvailable(): Promise<boolean> {
    if (!this.currentProvider) {
      return false;
    }
    return this.currentProvider.isApplePayAvailable();
  }

  /**
   * Check if Google Pay is available
   */
  async isGooglePayAvailable(): Promise<boolean> {
    if (!this.currentProvider) {
      return false;
    }
    return this.currentProvider.isGooglePayAvailable();
  }

  /**
   * Subscribe to billing events
   */
  on(event: any, handler: any): void {
    if (this.currentProvider) {
      this.currentProvider.on(event, handler);
    }
  }

  /**
   * Unsubscribe from billing events
   */
  off(event: any, handler: any): void {
    if (this.currentProvider) {
      this.currentProvider.off(event, handler);
    }
  }

  /**
   * Format price using current provider
   */
  formatPrice(amount: number, currency: string): string {
    if (!this.currentProvider) {
      // Fallback formatting
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency
      }).format(amount);
    }
    return this.currentProvider.formatPrice(amount, currency);
  }

  /**
   * Clean up all providers
   */
  destroy(): void {
    this.providers.forEach(provider => provider.destroy());
    this.providers.clear();
    this.currentProvider = null;
  }

  /**
   * Initialize provider based on paywall configuration
   */
  async initializeFromPaywallConfig(billing: 'paddle' | 'stripe'): Promise<void> {
    await this.initializeProvider(billing);
  }

  /**
   * Get provider name
   */
  getProviderName(): 'paddle' | 'stripe' | null {
    return this.currentProvider?.name || null;
  }

  /**
   * Check if any provider is initialized
   */
  isInitialized(): boolean {
    return this.currentProvider !== null && this.currentProvider.isInitialized();
  }
}

// Singleton instance
let billingServiceInstance: BillingService | null = null;

/**
 * Get or create the billing service instance
 */
export function getBillingService(config?: BillingServiceConfig): BillingService {
  if (!billingServiceInstance) {
    if (!config) {
      // Get configuration from environment
      const envConfig = getEnvConfig();
      config = {
        paddle: {
          clientToken: envConfig.paddle.clientToken,
          sandboxClientToken: envConfig.paddle.clientToken, // Use same token based on environment
          vendorId: envConfig.paddle.vendorId, // Legacy - kept for backward compatibility
          sandboxVendorId: envConfig.paddle.vendorId // Use same vendor ID based on environment
        },
        stripe: {
          publicKey: envConfig.stripe?.publicKey || '',
          sandboxPublicKey: envConfig.stripe?.publicKey || ''
        }
      };
    }
    billingServiceInstance = new BillingService(config);
  }
  return billingServiceInstance;
}

/**
 * Reset the billing service (useful for testing)
 */
export function resetBillingService(): void {
  if (billingServiceInstance) {
    billingServiceInstance.destroy();
    billingServiceInstance = null;
  }
}